/**
 * eventTextMeSave.ts — the database half of "💬 Text me about this one".
 * #1966, her item 11.
 *
 * ⚠️ ONE ROW PER EVENT, and that is a requirement rather than an optimisation.
 * The trigger `sync_event_reminder()` recomputes "the" reminder for an event;
 * `scheduled_tasks` renders every row under "Kin's scheduled tasks"; and two
 * rows for one event means two texts. So save is an UPSERT keyed on
 * `event_id`, never a bare insert — she can flip the toggle off and on, or
 * change the offset, any number of times.
 *
 * ⚠️ AND EVERY WRITE IS READ BACK. The `scheduled_tasks` INSERT policy is
 * `task_type in ('snack_mom','signup_item') or is_current_user_pro()`, so a
 * non-Pro insert is REFUSED BY RLS — which returns zero rows and NO ERROR.
 * PostgREST answers 200 with `[]`, supabase-js resolves, `error` is null. A
 * success toast over that is a claim about a row that does not exist (house
 * rule 16). `assertWriteLanded` is the shared check; the UI also disables the
 * toggle for a known-free account, and the two are not redundant:
 * `proBlocked = proLoaded && !isProTier` is FALSE while entitlements load
 * (#616, deliberate), and Pro can lapse between opening the sheet and saving.
 */

import { supabase } from "@/integrations/supabase/client";
import { assertWriteLanded } from "./scheduledTaskEdit";
import { retireScheduledTaskPatch } from "./retireScheduledTask";
import { buildEventTextMeRow, type EventTextMeBuild, type EventTextMeInput } from "./eventTextMeReminder";

/** The columns any caller needs to render the current state of the toggle. */
export interface ExistingEventTextMe {
  id: string;
  is_active: boolean | null;
  next_run_at: string | null;
  /** ⚠️ The row's config, RAW and whole.
   *
   *  Copilot on #1983. `retireEventTextMe` writes `{ ...config, ...stamp }`,
   *  so a caller that hands it a freshly-built `{ event_id }` REPLACES the
   *  config rather than stamping it — deleting the offset, the task
   *  description, the event title and the local date/time. Switching a
   *  30-minute reminder off and back on then showed the default 60, and the
   *  Tasks row lost the time it renders. The config has to travel. */
  config: Record<string, unknown>;
  offsetMinutes: number | null;
  /** True when the trigger or the builder had to move it out of quiet hours. */
  snapped: boolean;
  /** Set when something retired it — `quiet_hours_no_slot`, `event_in_past`,
   *  or `off` when she switched it off herself. */
  retiredReason: string | null;
}

/**
 * Whether Kin can actually TEXT her, independent of Pro.
 *
 * ⚠️ Copilot on #1983. Pro is the gate on WRITING the row; it says nothing
 * about delivery. `send-sms/index.ts:488-490` skips any account with no
 * verified phone or with `sms_opted_out` set, so a Pro user in either state
 * could save a reminder and be told "Kin will text you at 1:00 PM" about a
 * text that can never be sent.
 *
 * ⚠️ This is a UI check and NOT a bypass: the server still owns consent, and
 * STOP (`sms_opted_out`) is a legal opt-out that nothing here may override.
 * The point is only to stop the app promising what the server will refuse.
 */
export function canTextHer(prefs: {
  phone_verified_at?: string | null;
  sms_opted_out?: boolean | null;
} | null | undefined): boolean {
  if (!prefs) return false;
  return !!prefs.phone_verified_at && prefs.sms_opted_out !== true;
}

export type SaveEventTextMeResult =
  | { ok: true; taskId: string; snapped: boolean; localTime: string }
  /** The row could not be PLACED — nothing was written, and `reason` is the
   *  builder's. Distinct from a refused write, which is `write_refused`. */
  | { ok: false; kind: "unplaceable"; reason: Extract<EventTextMeBuild, { ok: false }>["reason"] }
  /** The write returned no row: RLS, or a vanished event. */
  | { ok: false; kind: "write_refused" };

function parseExisting(row: any): ExistingEventTextMe {
  const config = row?.config && typeof row.config === "object" ? row.config : {};
  const rawOffset = (config as Record<string, unknown>).offset_minutes;
  return {
    id: row.id,
    is_active: row.is_active ?? null,
    next_run_at: row.next_run_at ?? null,
    config: config as Record<string, unknown>,
    // ⚠️ `typeof === "number"`, not `|| null`: 0 is "at the time of the event"
    // and is a real choice, same trap as reminder_minutes on the event itself.
    offsetMinutes: typeof rawOffset === "number" ? rawOffset : null,
    snapped: (config as Record<string, unknown>).snapped_out_of_quiet_hours === true,
    retiredReason:
      typeof (config as Record<string, unknown>).retired_reason === "string"
        ? ((config as Record<string, unknown>).retired_reason as string)
        : null,
  };
}

/**
 * The text-me row for one event, or null.
 *
 * Reads by `event_id` — the column, not `config->>event_id` — because the
 * column is what the FK and the partial index are on.
 */
export async function fetchEventTextMe(eventId: string): Promise<ExistingEventTextMe | null> {
  if (!eventId) return null;
  const { data, error } = await (supabase.from as any)("scheduled_tasks")
    .select("id, is_active, next_run_at, config")
    .eq("event_id", eventId)
    .eq("task_type", "timed_reminder")
    .limit(1);
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : null;
  return row ? parseExisting(row) : null;
}

/**
 * Creates or moves the one text-me row for an event.
 *
 * ⚠️ The order is build-then-write. If the slot cannot be placed there is
 * nothing to tell the database, and she gets the real reason rather than a
 * refused write dressed up as a server problem.
 */
export async function saveEventTextMe(input: EventTextMeInput): Promise<SaveEventTextMeResult> {
  const built = buildEventTextMeRow(input);
  // ⚠️ `=== false`, not `!built.ok`. tsconfig.app.json has strictNullChecks
  // OFF deliberately (CLAUDE.md: "intentionally loose — DO NOT fix it"), and
  // without it TypeScript will not narrow a union on a bare truthiness test of
  // a boolean literal discriminant. `!built.ok` compiles and then fails to see
  // `built.reason`.
  if (built.ok === false) return { ok: false, kind: "unplaceable", reason: built.reason };

  const existing = await fetchEventTextMe(input.eventId);
  if (existing) return applyToExisting(existing.id, built);

  const { data, error } = await (supabase.from as any)("scheduled_tasks")
    .insert(built.row)
    .select("id");

  // ── ⚠️ 23505 — THE UNIQUE INDEX DID ITS JOB ──────────────────────────────
  //
  // Copilot on #1983. This function is read-then-write, so two interleaved
  // saves (two devices, or a double tap that beats the saving flag) can both
  // read "no reminder" and both insert.
  // `20261102190000_scheduled_tasks_one_event_reminder.sql` makes the second
  // one FAIL rather than create a duplicate — which would text her twice about
  // one event and be invisible to `fetchEventTextMe` (`.limit(1)`), to the off
  // switch, and to `sync_event_reminder()`.
  //
  // ⚠️ Losing that race is not an error to report. The row she wanted exists,
  // so re-read it and apply her values to it: the outcome is then identical
  // whichever write arrived first, which is the only version of this she could
  // ever make sense of.
  //
  // Degrades safely before the migration is run: with no unique index there is
  // no 23505, so this branch simply never fires and the behaviour is exactly
  // what it was.
  if (error && (error as { code?: string }).code === "23505") {
    const now = await fetchEventTextMe(input.eventId);
    if (now) return applyToExisting(now.id, built);
    // The conflicting row vanished between the failure and the re-read. One
    // retry, then stop — a loop here would spin against a constraint.
    const retry = await (supabase.from as any)("scheduled_tasks")
      .insert(built.row)
      .select("id");
    if (retry.error) throw retry.error;
    if (!Array.isArray(retry.data) || retry.data.length === 0) {
      return { ok: false, kind: "write_refused" };
    }
    return {
      ok: true,
      taskId: String(retry.data[0].id),
      snapped: built.snapped,
      localTime: built.localTime,
    };
  }

  if (error) throw error;
  try {
    assertWriteLanded(data, "text-me reminder");
  } catch {
    return { ok: false, kind: "write_refused" };
  }
  const id = Array.isArray(data) && data[0]?.id ? String(data[0].id) : "";
  return { ok: true, taskId: id, snapped: built.snapped, localTime: built.localTime };
}

/**
 * Applies her chosen values to the row that already exists.
 *
 * Shared by the ordinary "she changed the offset" path and the 23505 path, so
 * the two cannot drift into writing different things.
 */
async function applyToExisting(
  taskId: string,
  built: Extract<EventTextMeBuild, { ok: true }>,
): Promise<SaveEventTextMeResult> {
  // ⚠️ The built config does NOT carry `retired_reason`, which is what
  // re-arming a row she had switched off depends on: the trigger tests
  // `config ? 'retired_reason'` — key PRESENCE — so a key holding null would
  // still satisfy it. Writing the fresh config leaves the key absent, and
  // `is_active: true` below is her switching it back on explicitly.
  const config = { ...built.row.config } as Record<string, unknown>;
  const { data, error } = await (supabase.from as any)("scheduled_tasks")
    .update({
      next_run_at: built.row.next_run_at,
      schedule_time: built.row.schedule_time,
      display_name: built.row.display_name,
      is_active: true,
      config,
      updated_at: new Date().toISOString(),
    })
    .eq("id", taskId)
    .select("id");
  if (error) throw error;
  try {
    assertWriteLanded(data, "text-me reminder");
  } catch {
    return { ok: false, kind: "write_refused" };
  }
  return { ok: true, taskId, snapped: built.snapped, localTime: built.localTime };
}

/**
 * Switches it off.
 *
 * Retires rather than DELETEs, through the one shared helper. Clearing the
 * slot is half the job — the runner's due query has no staleness guard, so a
 * retired row holding a past slot fires the moment anything flips the flag
 * back on.
 *
 * ── ⚠️ WHY `retired_reason` IS DELETED AND NOT SET ─────────────────────────
 *
 * Copilot on #1983, and it corrects a comment here that claimed the opposite.
 * An earlier version stamped `retired_reason: "off"` and said that recorded
 * "she chose this", as distinct from a row the trigger retired itself.
 *
 * ⚠️ THE TRIGGER CANNOT TELL THE DIFFERENCE, because it tests for the KEY and
 * not its value. `sync_event_reminder()` re-arms with:
 *
 *     is_active = case
 *                   when task_row.is_active then true
 *                   when task_row.config ? 'retired_reason' then true
 *                   else false
 *                 end
 *
 * `config ? 'retired_reason'` is key PRESENCE. So the "off" stamp made her
 * deliberate opt-out re-arm on the next edit of the event — she switches the
 * text off, moves the event, and Kin texts her anyway. That is #577's
 * capability in reverse, and it is exactly the outcome the stamp was supposed
 * to prevent.
 *
 * Deleting the key lands on the `else false` arm, which is what "off and
 * staying off" looks like to that function. A row the TRIGGER retired still
 * carries its own `quiet_hours_no_slot` / `event_in_past` reason and still
 * re-arms, which is the behaviour that was always wanted.
 */
export async function retireEventTextMe(taskId: string, rawConfig: unknown): Promise<boolean> {
  const patch = retireScheduledTaskPatch(rawConfig, {
    // ⚠️ `undefined` DELETES the key — see retireScheduledTaskPatch. Setting
    // it to null would not: `config ? 'retired_reason'` is satisfied by a key
    // holding null, so the reminder would re-arm on the next event edit.
    retired_reason: undefined,
    snapped_out_of_quiet_hours: false,
  });
  const { data, error } = await (supabase.from as any)("scheduled_tasks")
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq("id", taskId)
    .select("id");
  if (error) throw error;
  return Array.isArray(data) && data.length > 0;
}
